import { useEffect, useState } from "react";
import {
  BlogsDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { BlogsService } from "../../services/blogs.service";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";

const Blogs = () => {
  const [blogs, setBlogs] = useState<BlogsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<BlogsDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    BlogsService.getAllBlogs()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: BlogsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setBlogs(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await BlogsService.getAllBlogs();
    setBlogs(
      updatedData?.data?.data?.map((item: BlogsDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        title: "",
        description: "",
        image: null,
        translations: [] as {
          lang_code: string;
          title: string;
          description: string;
        }[],
      },
      validationSchema: Yup.object({
        title: Yup.string().required("Bloqun başlığı tələb olunur"),
        description: Yup.string()
          .required("Bloqun təsviri tələb olunur")
          .min(10, "Təsvir ən azı 10 simvol olmalıdır."),
        image: Yup.string().required("Bloqun şəkili tələb olunur"),
      }),
      onSubmit: async (values) => {
        const azTranslation = {
          lang_code: "AZ",
          title: values.title,
          description: values.description,
        };

        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];

        const finalValues = {
          ...values,
          translations: updatedTranslations,
        };
        if (isEditing && editingRecord) {
          const isEqual =
            editingRecord.title === values.title &&
            editingRecord.description === values.description &&
            editingRecord.image === values.image &&
            editingRecord.translations === values.translations;
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            if (editingRecord.image === values.image) {
              const payload = {
                title: values.title,
                description: values.description,
                translations: updatedTranslations,
              };
              const response = await BlogsService.updateBlogs({
                ...payload,
                id: editingRecord.id,
              });
              if (response.status === 200) {
                message.success("Bloqu uğurla yeniləndi!");
                await handleAfterSubmit();
              } else {
                message.error(
                  response.errorMessage || "Bloqu yeniləmək alınmadı."
                );
              }
            } else {
              const response = await BlogsService.updateBlogs({
                ...finalValues,
                id: editingRecord.id,
              });
              if (response.status === 200) {
                message.success("Bloq uğurla yeniləndi!");
                await handleAfterSubmit();
              } else {
                message.error(
                  response.errorMessage || "Bloqu yeniləmək alınmadı."
                );
              }
            }
          }
        } else {
          const response = await BlogsService.addBlogs(finalValues);
          if (response.status === 201) {
            message.success("Bloq uğurla əlavə edildi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Bloq əlavə etmək alınmadı."
            );
          }
        }
        setLoading(false);
      },
    });
  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "title") {
        setFieldValue("title", value);
      } else if (field === "description") {
        setFieldValue("description", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          title: field === "title" ? value : "",
          description: field === "description" ? value : "",
        });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };
  const handleDeleteClick = async (record: BlogsDataType) => {
    try {
      await BlogsService.deleteBlogs(record.id.toString());
      setBlogs((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Bloq uğurla silindi!");
    } catch (error) {
      message.error("Bloqu silmək alınmadı.");
    }
  };
  const columns: TableProps<BlogsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Bloqun başlığı",
      dataIndex: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: "Bloq haqqında",
      dataIndex: "description",
      render: (_, record) => (
        <div>
          {record.description?.length > 20
            ? record.description.substring(0, 20) + "..."
            : record.description}
        </div>
      ),
    },
    {
      title: "Şəkil",
      dataIndex: "image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.image);
            setShowImageModal(true);
          }}
        >
          {record.image ? (
            <img
              style={{ width: "40px" }}
              src={record.image}
              alt={`${record.title.split(" ", 1)}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu bloqu siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setFieldValue("image", null);
    setLogoFileList([]);
  };
  const handleEditClick = (record: BlogsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("title", record.title);
    setFieldValue("description", record.description);
    setFieldValue("image", record.image);
    setFieldValue("translations", record.translations);
    setLangCode("AZ");
    setLogoFileList(
      record.image
        ? [
            {
              uid: "-1",
              name: `${record.title} image`,
              status: "done",
              url: record.image,
            },
          ]
        : []
    );
  };

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Bloqlar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Bloqlar</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<BlogsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={blogs}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Bloqu redaktə edin" : "Yeni bloq əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="title">
              Bloqun başlığı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="title"
                id="title"
                placeholder="Bloqun başlığı"
                value={
                  langCode === "AZ"
                    ? values.title
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title} touched={touched.title} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="Description">
              Bloqun təsviri {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="description"
                id="description"
                placeholder="Bloqun təsviri"
                value={
                  langCode === "AZ"
                    ? values.description
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.description || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.description}
              touched={touched.description}
            />
          </div>
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage error={errors.image} touched={touched.image} />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Blogs;
